import { AcademicStatusCell } from './components/AcademicStatusCell';
import {
  BookOutlined,
  FileDoneOutlined,
  PieChartOutlined,
  SolutionOutlined
} from '@ant-design/icons';
import {
  CanAccess,
  CrudFilters,
  HttpError,
  useNotification
} from '@refinedev/core';
import { CorporateReportFilterForm } from './components/CorporateReportFilterForm';
import { CorporateReportShell } from './components/CorporateReportShell';
import {
  DISCIPLINE_ACADEMIC_STATUS_DISPLAY,
  DISCIPLINE_ACADEMIC_STATUS_OPTIONS,
  ENROLLMENT_TYPE_OPTIONS
} from './constants';
import { EnrollmentStatusCell } from './components/EnrollmentStatusCell';
import { ExportButton, TextField, useSelect, useTable } from '@refinedev/antd';
import { Form, Grid, Input, Select, Table } from 'antd';
import { IDisciplineAcademicResult } from 'interfaces/corporate_reports';
import { IGraduateDiscipline } from 'interfaces/graduate_disciplines';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { ProgressPercentageCell } from './components/ProgressPercentageCell';
import { StudentInfoCell } from './components/StudentInfoCell';
import {
  buildCommonAcademicExportFilters,
  buildCommonAcademicSearchFilters,
  validateEnrollmentTypeSelection
} from './utils/filters';
import { useCallback, useEffect, useState } from 'react';
import { useCorporateCourseOptions } from './hooks/useCorporateCourseOptions';
import { useCorporateReportExport } from './hooks/useCorporateReportExport';

const REPORT_DESCRIPTION =
  'Este relatório permite à gestão corporativa acompanhar o rendimento dos alunos por disciplina,\ne não apenas pelo curso como um todo, oferecendo uma visão granular do desempenho.';

const COLUMN_LEGEND_ITEMS = [
  {
    label: 'Matrícula',
    description:
      '- Matrícula do aluno no curso.\n- Situação da matrícula do aluno no curso: Ativo ou Cancelado.',
    icon: <SolutionOutlined />,
    iconClassName: 'bg-violet-100 text-violet-700'
  },
  {
    label: 'Situação da disciplina',
    description:
      'Situação do aluno na disciplina: Em andamento, Aprovado, Reprovado, Reprovado por frequência,\nReprovado por progresso, Isento por aproveitamento, Cancelado ou Inativo.\nPermanece em andamento até o fechamento da turma.',
    icon: <BookOutlined />,
    iconClassName: 'bg-emerald-100 text-emerald-700'
  },
  {
    label: 'Nota',
    description:
      'Nota final do aluno na disciplina.\nA nota é disponibilizada somente após o fechamento da turma.',
    icon: <FileDoneOutlined />,
    iconClassName: 'bg-amber-100 text-amber-700'
  },
  {
    label: 'Progresso',
    description:
      'Percentual de conteúdos concluídos na disciplina. Atualizado conforme o aluno marca ou desmarca conteúdos como concluídos.',
    icon: <PieChartOutlined />,
    iconClassName: 'bg-sky-100 text-sky-700'
  }
];

interface IFilter {
  by_course_enrollment_type?: string;
  by_course_id?: string[];
  by_graduate_discipline_id?: string[];
  by_academic_status?: string[];
  by_user_name?: string;
  by_user_email?: string;
}

const REPORT_STORAGE_KEY = 'discipline_academic_results_id';
const getEnrollmentTypeLabel = (value?: string) =>
  ENROLLMENT_TYPE_OPTIONS.find(option => option.value === value)?.label ||
  value ||
  '-';

export const DisciplineAcademicResults = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const notification = useNotification();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.md;

  const { tableProps, searchFormProps } = useTable<
    IDisciplineAcademicResult,
    HttpError,
    IFilter
  >({
    resource: 'discipline_academic_results',
    queryOptions: { enabled: hasSearched },
    onSearch: params => {
      const filters = buildCommonAcademicSearchFilters(params);
      filters.push({
        field: 'filter[by_graduate_discipline_id][]',
        operator: 'in',
        value: params.by_graduate_discipline_id
      });

      return filters as CrudFilters;
    },
    sorters: {
      mode: 'server'
    }
  });
  const selectedEnrollmentType = Form.useWatch(
    'by_course_enrollment_type',
    searchFormProps.form
  );

  const { selectProps: graduateDisciplineOptions } =
    useSelect<IGraduateDiscipline>({
      resource: 'graduate_disciplines',
      optionLabel: 'name',
      optionValue: 'id',

      onSearch: value => [
        {
          field: 'filter[name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server',
        pageSize: 100
      }
    });

  const tablePropsWithConditionalData = hasSearched
    ? tableProps
    : { ...tableProps, dataSource: [], loading: false };
  const { courseOptions } = useCorporateCourseOptions(selectedEnrollmentType);

  useEffect(() => {
    searchFormProps.form?.setFieldValue('by_course_id');
  }, [searchFormProps.form, selectedEnrollmentType]);

  const { reportId, handleExportReport } = useCorporateReportExport<IFilter>({
    storageKey: REPORT_STORAGE_KEY,
    reportPath: '/admin/discipline_academic_results/report',
    searchFormProps,
    notification,
    buildExportFilters: formValues =>
      buildCommonAcademicExportFilters(formValues)
  });

  const validateFilter = useCallback(() => {
    return validateEnrollmentTypeSelection(
      searchFormProps.form?.getFieldValue('by_course_enrollment_type'),
      () =>
        notification.open?.({
          type: 'error',
          message: 'Modalidade obrigatória',
          description: 'Selecione a modalidade para aplicar o filtro.'
        })
    );
  }, [notification, searchFormProps.form]);

  return (
    <ListLayout
      title="Resultados Acadêmicos por Disciplina"
      headerActions={
        <CanAccess
          resource="corporate_reports/discipline_academic_results"
          action="list"
        >
          <ExportButton
            onClick={handleExportReport}
            disabled={!!reportId || !hasSearched || !selectedEnrollmentType}
            loading={!!reportId}
          >
            {!!reportId ? 'Relatório está sendo gerado' : 'Exportar'}
          </ExportButton>
        </CanAccess>
      }
    >
      <CorporateReportShell
        description={REPORT_DESCRIPTION}
        legendItems={COLUMN_LEGEND_ITEMS}
      >
        <CorporateReportFilterForm
          searchFormProps={searchFormProps}
          tableLoading={!!tableProps.loading}
          setHasSearched={setHasSearched}
          onValidateFilter={validateFilter}
        >
          <Form.Item className="mb-0 w-full sm:w-45" help={null}>
            <Form.Item
              name="by_course_enrollment_type"
              noStyle
              rules={[{ required: true, message: '' }]}
            >
              <Select
                allowClear
                placeholder="Modalidade"
                options={ENROLLMENT_TYPE_OPTIONS}
              />
            </Form.Item>
          </Form.Item>

          <Form.Item name="by_course_id" className="mb-0 w-full sm:w-45">
            <Select
              mode="multiple"
              allowClear
              disabled={!selectedEnrollmentType}
              style={{ width: '100%' }}
              placeholder="Curso"
              {...courseOptions}
            />
          </Form.Item>

          <Form.Item
            name="by_graduate_discipline_id"
            className="mb-0 w-full sm:w-45"
          >
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              placeholder="Disciplina"
              {...graduateDisciplineOptions}
            />
          </Form.Item>

          <Form.Item name="by_academic_status" className="mb-0 w-full sm:w-45">
            <Select
              mode="multiple"
              allowClear
              placeholder="Situação da disciplina"
              options={DISCIPLINE_ACADEMIC_STATUS_OPTIONS}
            />
          </Form.Item>

          <Form.Item name="by_user_name" className="mb-0 w-full sm:w-56">
            <Input placeholder="Nome" allowClear />
          </Form.Item>

          <Form.Item name="by_user_email" className="mb-0 w-full sm:w-56">
            <Input placeholder="E-mail" allowClear />
          </Form.Item>
        </CorporateReportFilterForm>

        <Table
          {...tablePropsWithConditionalData}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            ...tablePropsWithConditionalData.pagination,
            showSizeChanger: isDesktop,
            simple: !isDesktop,
            showTotal: (total, range) =>
              `Mostrando ${range[0]} a ${range[1]} de ${total} resultados`
          }}
        >
          <Table.Column
            dataIndex="user_name"
            key="user_name"
            title="Informações do aluno"
            sorter
            fixed={isDesktop ? 'left' : undefined}
            className="min-w-[180px] max-w-[250px]"
            render={(_, record) => (
              <StudentInfoCell
                userName={record.user_name}
                userEmail={record.user_email}
              />
            )}
          />
          <Table.Column
            dataIndex="course_enrollment_type"
            key="course_enrollment_type"
            title="Modalidade"
            width={140}
            render={value => (
              <TextField value={getEnrollmentTypeLabel(value)} />
            )}
          />
          <Table.Column
            dataIndex="course_name"
            key="course_name"
            title="Curso"
            className="min-w-[150px] max-w-[250px]"
            render={value => <TextField value={value} />}
          />
          <Table.Column
            dataIndex="discipline_name"
            key="discipline_name"
            title="Disciplina"
            sorter
            className="min-w-[150px] max-w-[250px]"
            render={value => <TextField value={value} />}
          />
          <Table.Column
            dataIndex="classroom_code"
            key="classroom_code"
            title="Turma"
            className="min-w-[120px] max-w-[200px]"
            render={value => <TextField value={value} />}
          />
          <Table.Column
            dataIndex="enrollment_status"
            key="enrollment_status"
            title="Matrícula"
            width={180}
            render={(_, record) => (
              <EnrollmentStatusCell
                enrollmentRegistration={record.enrollment_registration}
                enrollmentStatus={record.enrollment_status}
              />
            )}
          />

          <Table.Column
            dataIndex="academic_status"
            key="academic_status"
            title="Situação da disciplina"
            width={190}
            render={value => (
              <AcademicStatusCell
                status={value}
                statusConfig={DISCIPLINE_ACADEMIC_STATUS_DISPLAY}
              />
            )}
          />
          <Table.Column
            dataIndex="score"
            key="score"
            title="Nota"
            width={100}
            align="center"
            sorter
            render={value => <TextField value={value == null ? '-' : value} />}
          />
          <Table.Column
            dataIndex="attendance_rate"
            key="attendance_rate"
            title="Frequência"
            width={110}
            align="center"
            render={value => (
              <TextField value={value == null ? '-' : `${value}%`} />
            )}
          />
          <Table.Column
            dataIndex="progress_percentage"
            key="progress_percentage"
            title="Progresso"
            fixed={isDesktop ? 'right' : undefined}
            width={90}
            align="center"
            sorter
            render={value => <ProgressPercentageCell value={value} />}
          />
        </Table>
      </CorporateReportShell>
    </ListLayout>
  );
};
